import { all, dataOf, first, jq, on, ownAttr, text, whenVisible } from '../core/dom.js';
import { money } from '../core/money.js';

/**
 * Landing one-pager events (spec §2).
 *
 * 81.5% of traffic enters on a landing page, where there is no cart page and no
 * separate checkout page — the classic funnel simply does not apply. This family
 * reproduces the same drop-off granularity for the one-pager shape.
 *
 * Per spec §7.1 the /offer/ campaign pages (5,388 views a month) emit the same events;
 * `page_family` — set server-side to `landing` or `offer` — tells them apart.
 */

const SCROLL_THRESHOLDS = [25, 50, 75, 100];

export function collectLanding(tracker, { win = window, doc = document, selectors } = {}) {
  const family = tracker.context.page_family;

  if (family !== 'landing' && family !== 'offer') {
    return;
  }

  const landingId = tracker.context.landing_id || null;

  collectView(tracker, landingId, doc, selectors);

  // Off by default. Four thresholds on the 81.5% of traffic that lands on a one-pager is
  // several times the current total event volume, and the events table has no partitioning
  // and no retention yet. Switch it on per store once it does.
  if (tracker.context.scroll_depth) {
    collectScrollDepth(tracker, landingId, win, doc);
  }

  collectOfferSections(tracker, landingId, doc, selectors, win);
  collectAddToCart(tracker, landingId, doc, selectors);
  collectEmbeddedCheckout(tracker, landingId, win, doc, selectors);
}

export function collectView(tracker, landingId, doc = document, selectors = {}) {
  const container = first(selectors.landingContainer, doc);

  tracker.track(
    'lp_view',
    {
      landing_id: landingId,
      landing_slug: landingSlug(tracker.context.page_path),
      product_sku: dataOf(container, ['data-product-sku', 'data-sku'], null) || undefined,
      page_family: tracker.context.page_family,
    },
    { once: 'session', dedupeKey: 'lp_view:' + (landingId || tracker.context.page_path) }
  );
}

/**
 * On a one-pager the scroll position is the engagement signal that cart and checkout
 * page views provide elsewhere.
 */
export function collectScrollDepth(tracker, landingId, win = window, doc = document) {
  const reached = {};

  const check = () => {
    const el = doc.documentElement || doc.body;
    const height = Math.max(el.scrollHeight - el.clientHeight, 1);
    const pct = Math.min(100, Math.round(((win.pageYOffset || el.scrollTop || 0) / height) * 100));

    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (pct >= threshold && !reached[threshold]) {
        reached[threshold] = true;

        tracker.track('lp_scroll_depth', {
          landing_id: landingId,
          depth_pct: threshold,
        });
      }
    });
  };

  win.addEventListener('scroll', throttle(check, 250), { passive: true });
  check();
}

/**
 * lp_offer_viewed — a landing page stacks several offer blocks and without this there
 * is no way to know which one the visitor actually saw.
 */
export function collectOfferSections(tracker, landingId, doc = document, selectors = {}, win = window) {
  const sections = all(selectors.landingOfferSection, doc);

  sections.forEach((section, index) => {
    whenVisible(
      section,
      () => {
        tracker.track(
          'lp_offer_viewed',
          {
            landing_id: landingId,
            offer_id: offerId(section, index),
            // Document order at scan time. This used to read a data-offer-index attribute
            // first, described as template-authored and reorder-proof — but that attribute
            // appears in no commit of nf-platform, so the fallback was the only path that
            // ever ran. Positions therefore renumber if a template is reordered; get the
            // attribute emitted if that matters.
            section_position: index + 1,
          },
          { once: 'session', dedupeKey: 'lp_offer_viewed:' + (landingId || '') + ':' + offerId(section, index) }
        );
      },
      { ratio: 0.5, win }
    );
  });
}

/**
 * The landing module dispatches `nf:lp:cart-updated` with the full cart payload after
 * a successful nf_landing_add_to_cart, which is the only reliable success signal — the
 * button click alone can still fail server-side.
 */
export function collectAddToCart(tracker, landingId, doc = document, selectors = {}) {
  doc.addEventListener('nf:lp:cart-updated', (event) => {
    const payload = (event && event.detail && event.detail.cartPayload) || {};
    const last = payload.last_added_product || {};
    const items = payload.items || [];
    const item = items.length ? items[items.length - 1] : {};

    tracker.track('lp_add_to_cart', {
      landing_id: landingId,
      offer_id: String(selectedQty(doc, selectors) || ''),
      product_sku: last.sku || item.sku || '',
      product_id: Number(last.product_id || item.product_id || 0),
      quantity: Number(item.quantity || 1),
      price: money(last.price || item.price || 0),
      currency: last.currency || (payload.totals && payload.totals.currency) || '',
      cart_value: money((payload.totals && payload.totals.subtotal) || 0),
    });
  });

  // Pack/variant choice inside the offer block (spec §4 offer_option_selected has the
  // same trigger; here it is the landing-specific quantity choice).
  doc.addEventListener('nf:lp:option-selected', (event) => {
    const qty = (event && event.detail && event.detail.qty) || null;

    if (!qty) {
      return;
    }

    tracker.track('offer_option_selected', {
      landing_id: landingId,
      offer_id: 'pack-' + qty,
      option_id: String(qty),
      position: Number(qty),
    });
  });
}

/**
 * lp_checkout_started / lp_checkout_field_error / lp_payment_info_added.
 *
 * The embedded form is present in the DOM from the start, so "started" means the
 * customer actually scrolled it into view.
 */
export function collectEmbeddedCheckout(tracker, landingId, win = window, doc = document, selectors = {}) {
  const form = first(selectors.embeddedCheckout, doc);

  if (!form) {
    return;
  }

  const started = () => {
    tracker.track(
      'lp_checkout_started',
      { landing_id: landingId, offer_id: String(selectedQty(doc, selectors) || '') },
      { once: 'session', dedupeKey: 'lp_checkout_started:' + (landingId || '') }
    );
  };

  whenVisible(form, started, { ratio: 0.3, win });

  // Typing into the form is a start however little of it is on screen — a customer who
  // jumps there from an "order now" anchor can skip the moment it filled 30% of the view.
  on(doc, 'focusin', 'form.checkout input, form.checkout select, form.checkout textarea', started);

  on(doc, 'change', 'input[name="payment_method"]', (event, el) => {
    tracker.track(
      'lp_payment_info_added',
      { landing_id: landingId, payment_method: String(el.value || '') },
      { once: 'session', dedupeKey: 'lp_payment_info_added:' + (landingId || '') + ':' + el.value }
    );
  });

  // Native constraint validation covers the fields the browser checks itself.
  on(
    doc,
    'invalid',
    'form.checkout input, form.checkout select, form.checkout textarea',
    (event, el) => {
      reportFieldError(tracker, landingId, el.name || el.id || 'unknown', errorType(el));
    },
    true
  );

  const $ = jq(win);

  if ($ && typeof $ === 'function') {
    // And the server-side result, which is where most one-pager drop-off hides.
    $(doc.body).on('checkout_error', () => {
      errorNodes(doc).forEach((node) => {
        if (!text(node, 200)) {
          return;
        }

        reportFieldError(tracker, landingId, fieldFromMessage(node), 'server_validation');
      });
    });
  }
}

/**
 * One node per rendered error: WooCommerce lists them as `li` inside the notice, and
 * the container itself only counts when it holds no list.
 */
function errorNodes(doc) {
  return all('.woocommerce-error', doc).reduce((nodes, container) => {
    const items = all('li', container);

    return nodes.concat(items.length ? items : [container]);
  }, []);
}

/**
 * The message itself is never forwarded — a WooCommerce notice repeats what the
 * customer typed, so it can carry an e-mail address or a full postal address.
 */
function reportFieldError(tracker, landingId, fieldName, type) {
  // Only the landing-specific signal. `form_validation_error` used to be raised here as
  // well, but PHP already emits it from woocommerce_after_checkout_validation for the
  // same failure, keyed on the validation code — a key the browser cannot reproduce. So
  // every server-side checkout failure on a one-pager was counted twice, once as
  // `form_id: checkout` and once as `form_id: landing_checkout`, which read as two
  // populations rather than one counted twice. PHP owns that event now.
  tracker.track('lp_checkout_field_error', {
    landing_id: landingId,
    field_name: String(fieldName || 'unknown'),
    error_type: type,
  });
}

function errorType(el) {
  const validity = el.validity || {};

  if (validity.valueMissing) {
    return 'required';
  }

  if (validity.typeMismatch || validity.patternMismatch) {
    return 'invalid';
  }

  if (validity.tooShort || validity.tooLong || validity.rangeOverflow || validity.rangeUnderflow) {
    return 'length';
  }

  return 'other';
}

function fieldFromMessage(node) {
  const strong = node.querySelector && node.querySelector('strong');

  return strong ? text(strong, 60) : 'unknown';
}

function selectedQty(doc, selectors) {
  const active = first(withActive(selectors.landingOption, selectors.landingOptionActive), doc);

  if (active) {
    return dataOf(active, ['data-qty', 'data-value'], null) || text(active, 10);
  }

  // The merch layout scopes its radio name to the widget id, so both patterns are needed.
  const checked = doc.querySelector(
    'input[name^="nf_landing_qty"]:checked, input[name^="nf_lqty_merch_qty"]:checked, input[name="quantity"]:checked'
  );

  return checked ? checked.value : null;
}

/**
 * Appends the active-state class to every branch of a comma-separated selector.
 *
 * The previous version took only the first branch, so the selected pack was found on
 * option-1 landings and nowhere else.
 */
function withActive(selector, activeClass) {
  const suffix = activeClass || '.nf-landing__option--active';

  return String(selector || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part + suffix)
    .join(', ');
}

/**
 * Human-readable companion to landing_id. The id stays stable across A/B variants;
 * the slug is what an analyst recognises in a report.
 */
function landingSlug(path) {
  const parts = String(path || '').split('/').filter(Boolean);

  return parts.length ? parts[parts.length - 1] : '';
}

function offerId(section, index) {
  const explicit = ownAttr(section, ['data-offer-id', 'data-td-offer-id']);

  if (explicit) {
    return explicit;
  }

  const qty = ownAttr(section, ['data-qty']);

  // Same shape as the offer_id offer_option_selected reports, so lp_offer_viewed and the
  // pack choice can be joined on it — which is the whole point of having both events.
  if (qty) {
    return 'pack-' + qty;
  }

  return section.id || 'section-' + (index + 1);
}

function throttle(fn, wait) {
  let last = 0;
  let timer = null;

  return function throttled() {
    const now = Date.now();

    if (now - last >= wait) {
      last = now;
      fn();
    } else if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        last = Date.now();
        fn();
      }, wait);
    }
  };
}
